import { useState, type ChangeEvent, type JSX } from "react";
import { DynamicFieldsApi } from "@/services/api/dynamic-fields-api";
import type { TriyoDynamicField, TriyoDynamicFieldFile } from "@/types/models";
import { DealDateInput } from "./DealDateInput";
import { DealPeoplePicker } from "./DealPeoplePicker";
import {
  DYNAMIC_FIELD_TYPES,
  DYNAMIC_FILE_STATUS,
  TOGGLE_OFF,
  TOGGLE_ON,
  activeDynamicFieldFiles,
  dynamicFieldFileId,
  dynamicFieldFileName,
  isFileField,
  parseCheckboxValue,
  serializeCheckboxValue,
  visibleDynamicFields,
} from "./deal-dynamic-fields";

export interface DealDynamicFieldsProps {
  fields: TriyoDynamicField[];
  errors: Record<number, string>;
  /** Applies a new Data value to the field with this Id. */
  onChange: (fieldId: number, data: string) => void;
  /** Applies the complete File collection, including server removal markers. */
  onFilesChange?: (fieldId: number, files: TriyoDynamicFieldFile[]) => void;
  disabled?: boolean;
}

const VALID_FILE_EXTENSIONS = [
  ".doc",
  ".docx",
  ".ppt",
  ".pptx",
  ".xls",
  ".xlsx",
  ".pdf",
  ".jpeg",
  ".jpg",
  ".png",
  ".gif",
  ".tif",
  ".tiff",
  ".svg",
  ".csv",
  ".eml",
  ".msg",
] as const;
const MAX_FILE_SIZE = 25 * 1024 * 1024;

function inputId(field: TriyoDynamicField): string {
  return `create-deal-dynamic-${field.Id}`;
}

interface ControlProps {
  field: TriyoDynamicField;
  disabled: boolean;
  invalid: boolean;
  onChange: (data: string) => void;
  onFilesChange?: (files: TriyoDynamicFieldFile[]) => void;
}

function fileExtension(name: string): string {
  const index = name.lastIndexOf(".");
  return index >= 0 ? name.slice(index).toLowerCase() : "";
}

export interface FileUuidCrypto {
  randomUUID?: () => string;
  getRandomValues: (array: Uint8Array) => Uint8Array;
}

/** Builds a UUID v4 even on Outlook webviews that do not expose randomUUID. */
export function newFileId(cryptoApi: FileUuidCrypto = globalThis.crypto as FileUuidCrypto): string {
  if (typeof cryptoApi.randomUUID === "function") return cryptoApi.randomUUID();

  const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function readUploadedFile(file: File): Promise<TriyoDynamicFieldFile> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`${file.name} could not be read.`));
    reader.onload = () => {
      if (typeof reader.result !== "string") {
        reject(new Error(`${file.name} could not be read.`));
        return;
      }
      const id = newFileId();
      const extension = fileExtension(file.name);
      resolve({
        Id: id,
        uid: id,
        Name: file.name,
        name: file.name,
        MimeType: extension,
        extension,
        FileString: reader.result.split(",", 2)[1] ?? "",
        size: file.size,
        FileStatus: DYNAMIC_FILE_STATUS.uploaded,
        status: DYNAMIC_FILE_STATUS.uploaded,
      });
    };
    reader.readAsDataURL(file);
  });
}

function fileDataUrl(file: TriyoDynamicFieldFile): string | null {
  const content = file.FileString?.trim();
  if (!content) return null;
  if (content.startsWith("data:")) return content;

  const mimeType = file.MimeType?.includes("/") ? file.MimeType : "application/octet-stream";
  return `data:${mimeType};base64,${content}`;
}

function saveFile(file: TriyoDynamicFieldFile): boolean {
  const href = fileDataUrl(file);
  if (!href) return false;

  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = dynamicFieldFileName(file);
  anchor.hidden = true;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  return true;
}

function DynamicFileField({
  field,
  invalid,
  disabled,
  onFilesChange,
}: Pick<ControlProps, "field" | "invalid" | "disabled" | "onFilesChange">): JSX.Element {
  const files = activeDynamicFieldFiles(field.Files);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState("");
  const [uploadError, setUploadError] = useState("");

  const download = async (file: TriyoDynamicFieldFile, index: number): Promise<void> => {
    const id = dynamicFieldFileId(file);
    const requestKey = id ?? String(index);
    setDownloadingId(requestKey);
    setDownloadError("");

    try {
      let downloadable = file;
      if (!file.FileString && id) {
        const hydrated = await DynamicFieldsApi.getDynamicFormFieldFile(id);
        downloadable = {
          ...file,
          FileString: hydrated?.FileString ?? null,
          MimeType: hydrated?.MimeType || file.MimeType,
        };
      }

      if (!saveFile(downloadable)) {
        setDownloadError(`${dynamicFieldFileName(file, `Attachment ${index + 1}`)} could not be downloaded.`);
      }
    } catch {
      setDownloadError(`${dynamicFieldFileName(file, `Attachment ${index + 1}`)} could not be downloaded.`);
    } finally {
      setDownloadingId(null);
    }
  };

  const addFiles = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const input = event.currentTarget;
    const picked = Array.from(input.files ?? []);
    input.value = "";
    if (picked.length === 0 || !onFilesChange) return;

    const invalidFiles = picked.filter((file) => {
      const extension = fileExtension(file.name);
      return (
        !VALID_FILE_EXTENSIONS.includes(extension as (typeof VALID_FILE_EXTENSIONS)[number]) ||
        file.size > MAX_FILE_SIZE
      );
    });
    const validFiles = picked.filter((file) => !invalidFiles.includes(file));
    setUploadError(
      invalidFiles.length > 0 ? "Some files were skipped. Use a supported file type with a maximum size of 25 MB." : ""
    );

    try {
      const uploaded = await Promise.all(validFiles.map(readUploadedFile));
      if (uploaded.length > 0) onFilesChange([...(field.Files ?? []), ...uploaded]);
    } catch (error: unknown) {
      setUploadError(error instanceof Error ? error.message : "The selected files could not be read.");
    }
  };

  const removeFile = (target: TriyoDynamicFieldFile): void => {
    if (!onFilesChange) return;
    const next = (field.Files ?? []).flatMap((file) => {
      if (file !== target) return [file];
      if (file.FileStatus === DYNAMIC_FILE_STATUS.uploaded || file.status === DYNAMIC_FILE_STATUS.uploaded) return [];
      return [
        {
          ...file,
          FileString: "",
          FileStatus: DYNAMIC_FILE_STATUS.removing,
          status: DYNAMIC_FILE_STATUS.removing,
        },
      ];
    });
    onFilesChange(next);
  };

  return (
    <div
      id={inputId(field)}
      className="create-deal__dynamic-files"
      role="list"
      aria-labelledby={`${inputId(field)}-label`}
      aria-invalid={invalid || undefined}
      aria-describedby={invalid ? `${inputId(field)}-error` : undefined}
    >
      {!disabled && onFilesChange ? (
        <input
          type="file"
          multiple
          accept={VALID_FILE_EXTENSIONS.join(",")}
          aria-label={`Add files to ${field.Label || "File field"}`}
          onChange={(event) => void addFiles(event)}
        />
      ) : null}
      {files.length === 0 ? <p className="create-deal__dynamic-files-empty">No files attached.</p> : null}
      {files.map((file, index) => {
        const id = dynamicFieldFileId(file);
        const name = dynamicFieldFileName(file, `Attachment ${index + 1}`);
        const requestKey = id ?? String(index);
        return (
          <div className="create-deal__dynamic-file" role="listitem" key={`${requestKey}-${name}`}>
            <span title={name}>{name}</span>
            <button
              type="button"
              className="create-deal__dynamic-file-download"
              disabled={downloadingId === requestKey || (!file.FileString && !id)}
              aria-label={`Download ${name}`}
              onClick={() => void download(file, index)}
            >
              {downloadingId === requestKey ? "Downloading…" : "Download"}
            </button>
            {!disabled && onFilesChange ? (
              <button
                type="button"
                className="create-deal__dynamic-file-remove"
                aria-label={`Remove ${name}`}
                onClick={() => removeFile(file)}
              >
                Remove
              </button>
            ) : null}
          </div>
        );
      })}
      {uploadError ? (
        <p className="create-deal__field-error" role="alert">
          {uploadError}
        </p>
      ) : null}
      {downloadError ? (
        <p className="create-deal__field-error" role="alert">
          {downloadError}
        </p>
      ) : null}
    </div>
  );
}

/** Renders the input for one field, matching Triyo.Web's per-FieldType control. */
function DynamicFieldControl({ field, disabled, invalid, onChange, onFilesChange }: ControlProps): JSX.Element {
  const value = String(field.Data ?? "");
  const options = (field.LookupValues ?? []).filter(Boolean);
  const common = {
    id: inputId(field),
    disabled,
    "aria-invalid": invalid || undefined,
    "aria-describedby": invalid ? `${inputId(field)}-error` : undefined,
  };

  switch (field.FieldType) {
    case DYNAMIC_FIELD_TYPES.file:
      return <DynamicFileField field={field} invalid={invalid} disabled={disabled} onFilesChange={onFilesChange} />;

    case DYNAMIC_FIELD_TYPES.peoplePicker:
      return (
        <DealPeoplePicker
          fieldId={field.Id}
          value={value}
          disabled={disabled}
          invalid={invalid}
          describedBy={invalid ? `${inputId(field)}-error` : undefined}
          onChange={onChange}
        />
      );

    case DYNAMIC_FIELD_TYPES.multiText:
      return (
        <textarea
          {...common}
          value={value}
          rows={3}
          placeholder={field.Placeholder || undefined}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    case DYNAMIC_FIELD_TYPES.dropDown:
      return (
        <select {...common} value={value} onChange={(event) => onChange(event.target.value)}>
          <option value="">- Select -</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );

    case DYNAMIC_FIELD_TYPES.radioButtons:
      return (
        <div className="create-deal__dynamic-options" role="radiogroup" aria-label={field.Label ?? undefined}>
          {options.map((option) => (
            <label key={option} className="create-deal__dynamic-option">
              <input
                type="radio"
                name={inputId(field)}
                value={option}
                checked={value === option}
                disabled={disabled}
                onChange={() => onChange(option)}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      );

    case DYNAMIC_FIELD_TYPES.checkbox: {
      const selected = parseCheckboxValue(value);
      return (
        <div className="create-deal__dynamic-options" role="group" aria-label={field.Label ?? undefined}>
          {options.map((option) => (
            <label key={option} className="create-deal__dynamic-option">
              <input
                type="checkbox"
                value={option}
                checked={selected.includes(option)}
                disabled={disabled}
                onChange={() =>
                  onChange(
                    serializeCheckboxValue(
                      selected.includes(option) ? selected.filter((entry) => entry !== option) : [...selected, option]
                    )
                  )
                }
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      );
    }

    case DYNAMIC_FIELD_TYPES.toggle:
      return (
        <label className="create-deal__dynamic-toggle">
          <input
            {...common}
            type="checkbox"
            checked={value === TOGGLE_ON}
            onChange={(event) => onChange(event.target.checked ? TOGGLE_ON : TOGGLE_OFF)}
          />
          <span>{value === TOGGLE_ON ? "Yes" : "No"}</span>
        </label>
      );

    case DYNAMIC_FIELD_TYPES.integer:
    case DYNAMIC_FIELD_TYPES.decimal:
      return (
        <input
          {...common}
          type="number"
          step={field.FieldType === DYNAMIC_FIELD_TYPES.integer ? "1" : "any"}
          value={value}
          placeholder={field.Placeholder || undefined}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    case DYNAMIC_FIELD_TYPES.date:
      return (
        <DealDateInput
          {...common}
          value={value ? value.slice(0, 10) : ""}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    case DYNAMIC_FIELD_TYPES.time:
      return <input {...common} type="time" value={value} onChange={(event) => onChange(event.target.value)} />;

    case DYNAMIC_FIELD_TYPES.url:
      return (
        <input
          {...common}
          type="url"
          value={value}
          placeholder={field.Placeholder || "https://"}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    default:
      return (
        <input
          {...common}
          type="text"
          value={value}
          placeholder={field.Placeholder || undefined}
          onChange={(event) => onChange(event.target.value)}
        />
      );
  }
}

/**
 * The dynamic fields configured on the selected Deal template. Renders nothing
 * when the template has none, so templates without dynamic fields look exactly
 * as they did before.
 */
export function DealDynamicFields({
  fields,
  errors,
  onChange,
  onFilesChange,
  disabled = false,
}: DealDynamicFieldsProps): JSX.Element | null {
  const shown = visibleDynamicFields(fields);
  if (shown.length === 0) return null;

  return (
    <div className="create-deal__dynamic">
      {shown.map((field) => {
        const error = errors[field.Id];
        const fileField = isFileField(field);
        return (
          <div key={field.Id} className="create-deal__field">
            <label
              className="create-deal__field-label"
              id={`${inputId(field)}-label`}
              htmlFor={fileField ? undefined : inputId(field)}
            >
              {field.Label}
              {field.Required ? <span className="create-deal__required"> *</span> : null}
            </label>
            <DynamicFieldControl
              field={field}
              disabled={disabled}
              invalid={!!error}
              onChange={(data) => onChange(field.Id, data)}
              onFilesChange={onFilesChange ? (files) => onFilesChange(field.Id, files) : undefined}
            />
            {field.FieldHelp ? <p className="create-deal__dynamic-help">{field.FieldHelp}</p> : null}
            {error ? (
              <p className="create-deal__field-error" id={`${inputId(field)}-error`}>
                {error}
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
